import React from "react";
import { Link } from "react-router-dom";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/delivery.scss";

function Delivery() {
  return (
    <main className="policy-page policy-page--shipping">
      <header className="policy-hero">
        <div>
          <p className="policy-eyebrow">Customer care · {siteConfig.brandName}</p>
          <h1>Shipping &amp; delivery</h1>
        </div>
        <div className="policy-hero__intro">
          <p>
            A clear guide to dispatch times, estimated delivery and what happens
            after your order leaves the studio.
          </p>
          <span>Last updated 13 August 2026</span>
        </div>
      </header>

      <div className="policy-shell">
        <aside className="policy-sidebar">
          <nav aria-label="Customer policies">
            <p>Customer policies</p>
            <Link className="is-active" to="/shipping" aria-current="page">
              Shipping &amp; delivery
            </Link>
            <Link to="/returns">Returns &amp; refunds</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </nav>
          <nav className="policy-toc" aria-label="On this page">
            <p>On this page</p>
            <a href="#shipping-dispatch">Dispatch</a>
            <a href="#shipping-rates">Rates &amp; estimates</a>
            <a href="#shipping-tracking">Tracking</a>
            <a href="#shipping-international">International orders</a>
            <a href="#shipping-problems">Delivery problems</a>
          </nav>
        </aside>

        <article className="policy-content">
          <div className="policy-demo-note" role="note">
            <strong>Demo storefront notice</strong>
            <p>
              These rates and timelines are illustrative frontend content. No
              shipment is booked and no live carrier quote is requested by this demo.
            </p>
          </div>

          <section id="shipping-dispatch">
            <p className="policy-section-number">01</p>
            <h2>Preparing your order</h2>
            <p>
              Orders are prepared during studio business hours. Our current sample
              dispatch window is <strong>{siteConfig.shipping.dispatchDays}</strong>.
              Weekends and public holidays begin processing on the next business day.
            </p>
            <p>
              Once an order is packed, the production storefront would send a
              dispatch email with the carrier and tracking reference. Delivery
              estimates begin from dispatch, not from the moment an order is placed.
            </p>
          </section>

          <section id="shipping-rates">
            <p className="policy-section-number">02</p>
            <h2>Rates &amp; delivery estimates</h2>
            <p>
              Final services depend on destination, parcel size and carrier
              availability. The checkout screen should always be treated as the
              final rate in a connected production store.
            </p>
            <div className="policy-cards policy-cards--three">
              <div className="policy-card">
                <span>Standard</span>
                <strong>{formatPrice(siteConfig.shipping.standardRate)}</strong>
                <p>Sample tracked service for eligible destinations.</p>
              </div>
              <div className="policy-card">
                <span>Complimentary</span>
                <strong>From {formatPrice(siteConfig.shipping.freeThreshold)}</strong>
                <p>Applied to qualifying baskets before optional surcharges.</p>
              </div>
              <div className="policy-card">
                <span>Oversized &amp; fragile</span>
                <strong>Quoted at checkout</strong>
                <p>Extra protection or a specialist carrier may be required.</p>
              </div>
            </div>
            <p className="policy-small-print">
              Estimated transit: Hong Kong 2–4 business days; most supported Asian
              destinations 3–7 business days; other supported destinations 5–12 business days. These
              are sample estimates, not guarantees.
            </p>
          </section>

          <section id="shipping-tracking">
            <p className="policy-section-number">03</p>
            <h2>Tracking your parcel</h2>
            <p>
              Tracking may take up to 24 hours to update after a label is created.
              During busy periods, carrier scans can appear later than the physical
              movement of a parcel.
            </p>
            <ul>
              <li>Check the delivery address before completing checkout.</li>
              <li>Use the carrier link supplied in the dispatch email.</li>
              <li>Allow one full business day after the estimated delivery date.</li>
            </ul>
          </section>

          <section id="shipping-international">
            <p className="policy-section-number">04</p>
            <h2>International orders</h2>
            <p>
              Local taxes or destination handling fees may apply where required.
              Any charge shown by the payment provider forms part of the final order
              total; carrier delays remain outside the seller’s control.
            </p>
            <p>
              Some fragile or restricted objects may not be available in every
              destination. A production checkout would prevent unsupported services
              before payment.
            </p>
          </section>

          <section id="shipping-problems">
            <p className="policy-section-number">05</p>
            <h2>Delays, damage or a missing parcel</h2>
            <p>
              If a production order arrived damaged, keep the packaging and photograph
              both the parcel and item. For a delayed parcel, contact the team with the
              order reference after checking the latest carrier scan.
            </p>
            <div className="policy-contact-box">
              <div>
                <span>Need a hand?</span>
                <strong>{siteConfig.contact.hours}</strong>
              </div>
              <Link to="/contact">Contact customer care</Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
